/**
 * patientStatusCopy.ts — patient-facing labels for dashboard wire states.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: patient status copy
 * normalisation (display only).
 *
 * WHY: Orders `patientDashboard` returns subscription / order-case / payment
 * enums (`dormant`, `awaiting_approval`, `held`, …). Those are ops language and
 * must never render raw (or SCREAMING uppercase) on patient surfaces. This
 * module maps wire values → label + badge tone (+ optional short detail).
 * Backend enums stay unchanged; authoritative medication catalogue names remain
 * an Orders/prices follow-up — when `medicationName === productKey` we only
 * humanise the key for display (e.g. `starter_plus` → `Starter Plus`).
 *
 * Payment wording aligns with checkout `paymentCopy.ts`: a Route 1 `held`
 * phase is authorised, NOT charged / NOT "Paid".
 */
import type { BadgeTone } from '../ui/kit';

/** Result of mapping a wire status for a patient StatusBadge. */
export interface PatientStatusCopy {
  /** Patient-readable primary label (never a raw enum). */
  label: string;
  /** Kit badge tone — colour is secondary; the label carries meaning. */
  tone: BadgeTone;
  /** Optional one-line clarification shown beside/under the badge when the surface allows. */
  detail?: string;
}

/** Live subscription states on `patientDashboard.currentPlan.state`. */
export type SubscriptionWireState = 'dormant' | 'active' | 'past_due' | 'cancelled' | 'completed';

/** Common order-case states patients see on the dashboard history. */
export type OrderWireState =
  | 'awaiting_approval'
  | 'awaiting_payment'
  | 'approved'
  | 'rejected'
  | 'awaiting_dispatch'
  | 'dispatched'
  | 'delivered'
  | 'cancelled';

/** PaymentRecord phases on `patientDashboard.bills[].phase`. */
export type PaymentWirePhase = 'held' | 'captured' | 'released' | 'refunded' | 'failed';

/** PaymentRecord kinds on `patientDashboard.bills[].kind`. */
export type PaymentWireKind = 'month1_preauth' | 'callback_49' | 'renewal';

/**
 * Turn an unknown snake_case / kebab wire token into Title case words.
 * Never returns the raw underscore form; never uppercases the whole string.
 */
export function humaniseWireToken(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return 'Unknown';
  return trimmed
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Display a treatment name without inventing a catalogue.
 * When Orders still echoes `productKey` as `medicationName`, title-case the key
 * (e.g. `starter_plus` → `Starter Plus`). Real display names pass through unchanged.
 */
export function displayTreatmentName(medicationName: string, productKey?: string): string {
  const name = medicationName.trim();
  const key = productKey?.trim();
  if (!name && !key) return 'Your treatment';
  if (!name && key) return humaniseWireToken(key);
  if (key && name === key) return humaniseWireToken(key);
  // Opaque lowercase slug with no spaces — treat as a key, not a curated name.
  if (/^[a-z0-9]+(?:[_-][a-z0-9]+)*$/.test(name)) return humaniseWireToken(name);
  return name;
}

export function planStatusCopy(state: string): PatientStatusCopy {
  switch (state) {
    case 'dormant':
      return { label: 'Awaiting clinician approval', tone: 'pending' };
    case 'active':
      return { label: 'Active', tone: 'positive' };
    case 'past_due':
      return { label: 'Payment needs attention', tone: 'danger' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'muted' };
    case 'completed':
      return { label: 'Completed', tone: 'muted' };
    default:
      return { label: humaniseWireToken(state), tone: 'neutral' };
  }
}

export function orderStatusCopy(state: string): PatientStatusCopy {
  switch (state) {
    case 'awaiting_approval':
      return { label: 'Awaiting clinician approval', tone: 'pending' };
    case 'awaiting_payment':
      return { label: 'Payment incomplete', tone: 'pending' };
    case 'approved':
      return { label: 'Approved', tone: 'positive' };
    case 'rejected':
      return { label: 'Not approved', tone: 'danger' };
    case 'awaiting_dispatch':
      return { label: 'Preparing to send', tone: 'pending' };
    /* ── Fulfilment tail (pharmacy dispensing lane, 2026-07-29) ───────────── */
    case 'awaiting_dispense':
      return { label: 'Preparing your order', tone: 'pending' };
    case 'dispensed':
      return { label: 'Ready to send', tone: 'positive', detail: 'Waiting for the courier' };
    /*
     * A pharmacy REFUSED to dispense — and the patient must never learn that.
     * The rejection goes to the GP Service admin team, who re-route the order;
     * from the patient's side nothing has gone wrong yet. So this reads
     * IDENTICALLY to `awaiting_dispense`: deliberately indistinguishable, not
     * a lazy copy-paste. Without this case the default branch would humanise
     * the raw enum into "Dispense Rejected" on the patient's own dashboard,
     * which is precisely the disclosure the CTO ruled out.
     */
    case 'dispense_rejected':
      return { label: 'Preparing your order', tone: 'pending' };
    case 'dispatched':
      return { label: 'Dispatched', tone: 'positive' };
    case 'delivered':
      return { label: 'Delivered', tone: 'positive' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'muted' };
    default:
      return { label: humaniseWireToken(state), tone: 'neutral' };
  }
}

/**
 * Payment phase → patient copy.
 * `held` must never read as Paid/Charged (Route 1 pre-auth until clinician approval).
 */
export function paymentPhaseCopy(phase: string): PatientStatusCopy {
  switch (phase) {
    case 'held':
      return {
        label: 'Payment authorised',
        tone: 'pending',
        detail: 'Not charged yet',
      };
    case 'captured':
      return { label: 'Paid', tone: 'positive' };
    case 'released':
      return { label: 'Hold released', tone: 'muted', detail: 'Not charged' };
    case 'refunded':
      return { label: 'Refunded', tone: 'muted' };
    case 'failed':
      return { label: 'Payment failed', tone: 'danger' };
    default:
      return { label: humaniseWireToken(phase), tone: 'neutral' };
  }
}

/** Patient-safe description for a bill row (replaces fixture-only `description`). */
export function paymentKindLabel(kind: string): string {
  switch (kind) {
    case 'month1_preauth':
      return 'First-month payment';
    case 'callback_49':
      return 'Consultation fee';
    case 'renewal':
      return 'Renewal payment';
    default:
      return humaniseWireToken(kind);
  }
}
